import React, { FC, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { MultiColumnDropdownWithLabel } from '@blueprint/multicolumn-dropdown';
import { getTranslator } from '../../i18n/getTranslator';
import {
    ForSalePriceMinSelectOptions,
    ForSalePriceMaxSelectOptions
} from '../../../search/common/SelectPriceOptions';
import searchTranslations from 'public/locales/en/search.json';
import commonTranslations from 'public/locales/en/common.json';
import {
    updateFilter,
    minPriceSelector,
    maxPriceSelector
} from '../../../reducers/filters';
import { DropdownContainer } from '../../../search/common/DropdownContainer';
import { buildSelectPriceTitleForSale } from '../../../search/helpers';

const t = getTranslator(searchTranslations);
const tCommon = getTranslator(commonTranslations);

export const SelectPriceForSale: FC = () => {
    const reduxDispatch = useDispatch();
    const {
        selectedValue: priceMinSelectedValue,
        options: priceMinOptions
    } = useSelector(minPriceSelector);

    const {
        selectedValue: priceMaxSelectedValue,
        options: priceMaxOptions
    } = useSelector(maxPriceSelector);

    const onMaxPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('maxPrice', evt.currentTarget.value));
    };

    const onMinPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('minPrice', evt.currentTarget.value));
    };

    const buttonText = buildSelectPriceTitleForSale(
        priceMinSelectedValue,
        priceMaxSelectedValue,
        tCommon,
        t
    );

    const priceMinInt = parseInt(priceMinSelectedValue || '0', 10);
    const priceMaxInt = parseInt(priceMaxSelectedValue || '0', 10);

    const priceRangeLabel = t('filters.priceRange');
    const minPriceLabelText = t('filters.minPrice');
    const PriceOptionLabel = t('filters.field.price.label');
    const maxPriceLabelText = t('filters.maxPrice');

    return (
        <MultiColumnDropdownWithLabel
            title={buttonText}
            label={priceRangeLabel}
            testId="AnyPrice_testId"
            isLeft
        >
            <DropdownContainer>
                <DropdownWithLabel
                    id="price_min"
                    onChange={onMinPriceChange}
                    value={priceMinSelectedValue}
                    name="price_min"
                    hasBorderRadius
                    aria-label="Min Price"
                    data-testid="min_price_testid"
                    label={minPriceLabelText}
                >
                    {priceMinOptions ? (
                        <ForSalePriceMinSelectOptions
                            options={priceMinOptions}
                            priceOtherInt={priceMaxInt}
                            keyPrefix="pricemin"
                        />
                    ) : (
                        <option>{PriceOptionLabel}</option>
                    )}
                </DropdownWithLabel>
            </DropdownContainer>
            <DropdownContainer>
                <DropdownWithLabel
                    id="price_max"
                    onChange={onMaxPriceChange}
                    value={priceMaxSelectedValue}
                    name="price_max"
                    hasBorderRadius
                    aria-label="Max Price"
                    data-testid="max_price_testid"
                    label={maxPriceLabelText}
                >
                    {priceMaxOptions ? (
                        <ForSalePriceMaxSelectOptions
                            options={priceMaxOptions}
                            priceOtherInt={priceMinInt}
                            keyPrefix="pricemax"
                        />
                    ) : (
                        <option>{PriceOptionLabel}</option>
                    )}
                </DropdownWithLabel>
            </DropdownContainer>
        </MultiColumnDropdownWithLabel>
    );
};
